import { useEffect, useRef, useState } from "react";
import { AlertCircle, X } from "lucide-react";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png"];

const ImageUploader = ({ onChange }) => {
  const fileInputRef = useRef(null);
  const objectUrlRef = useRef(null);

  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");

  const clearPreviewUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  };

  const handleFile = (file) => {
    setError("");

    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      clearPreviewUrl();
      setPreview(null);
      setError("Only JPG and PNG images are allowed.");
      onChange(null);
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      clearPreviewUrl();
      setPreview(null);
      setError("Image size must be 5 MB or less.");
      onChange(null);
      return;
    }

    clearPreviewUrl();

    const imageUrl = URL.createObjectURL(file);
    objectUrlRef.current = imageUrl;

    setPreview(imageUrl);
    onChange(file);
  };

  const handleChange = (event) => {
    const file = event.target.files?.[0];
    handleFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    handleFile(file);
  };

  const removeImage = (event) => {
    event.stopPropagation();
    clearPreviewUrl();
    setPreview(null);
    setError("");
    onChange(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(
    () => () => {
      clearPreviewUrl();
    },
    []
  );

  return (
    <div className="w-full">
      <label className="mb-2 block text-sm text-muted-foreground">
        Upload a clear image of the item you want to post.
      </label>

      <div
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`relative flex h-80 w-full cursor-pointer items-center justify-center rounded-2xl border-2 border-dashed transition-all ${
          isDragging
            ? "border-primary bg-primary/10"
            : error
            ? "border-destructive bg-destructive/5"
            : "border-border bg-background hover:border-primary/50"
        }`}
      >
        <input
          type="file"
          accept="image/jpeg,image/png"
          ref={fileInputRef}
          onChange={handleChange}
          className="hidden"
        />

        {preview ? (
          <>
            <img
              src={preview}
              alt="Preview"
              className="h-full w-full rounded-2xl object-cover"
            />

            <button
              type="button"
              onClick={removeImage}
              aria-label="Remove selected image"
              className="absolute right-3 top-3 rounded-full bg-black/70 p-2 text-white hover:bg-black"
            >
              <X size={16} />
            </button>
          </>
        ) : (
          <div className="px-4 text-center">
            <div className="text-sm text-muted-foreground">
              Drag & drop your image here
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              or click to browse
            </div>
            <div className="mt-3 text-sm font-medium text-primary">
              JPG, PNG up to 5MB
            </div>
          </div>
        )}
      </div>

      {error && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
